import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import React, { ChangeEvent, FC, FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import { useCreateRoleMutation, useUpdateRoleMutation, useGetAllPermissionsQuery, useGetRoleByIdQuery } from "./rolesApi";
import { allPermissionCheckedOnColumns, allPermissionChecked, filterPermission } from "../../../helpers/utils/RoleUtils";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import { Routes as AppRoutes } from "../../../routes";
import { FaCopy } from "react-icons/fa6";

import ButtonTray from "../../../components/common/form/ButtonTray";
import IconButton from "../../../components/common/form/IconButton";
import Preloader from "../../../components/common/Preloader";
import { history } from "../../../helpers/utils/history";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
    roleId?: any;
    roleByIdQuery?: any,
    roleData?: any,
    setRoleData?: any
}

const RoleForm: FC<props> = ({
    crumbs,
    title,
    btnText,
    isReadOnly,
    roleId,
    roleByIdQuery,
    roleData,
    setRoleData
}) => {

    const navigate = useNavigate();
    let [permissions, setPermissions] = useState<any>([]);
    let [permissionLabel, setPermissionsLabel] = useState<any>([]);


    const [createRole, createRoleApiResponse] = useCreateRoleMutation();
    const [updateRole, updateRoleApiResponse] = useUpdateRoleMutation();
    const permissionsQuery = useGetAllPermissionsQuery("");

    useEffect(() => {
        if (permissionsQuery?.data) {
            // Extract unique permission labels
            const labels = [...new Set(permissionsQuery.data.map((item: any) => item.name.split(' ')[0]))];
            setPermissionsLabel(labels);

            if (roleId && roleByIdQuery?.data) {
                // Create permissions with isChecked flag based on roleByIdQuery
                const updatedPermissions = permissionsQuery.data.map((item: any) => ({
                    ...item,
                    isChecked: roleByIdQuery.data.permissions.includes(item.name),
                }));

                setPermissions(updatedPermissions);
            } else {
                // If no roleId, set permissions without isChecked flag
                setPermissions(permissionsQuery.data);

            }
        }
    }, [permissionsQuery?.data, roleByIdQuery?.data, roleId]);


    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setRoleData({
            ...roleData,
            [name]: value
        });
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Create the common object
        const commonObj = {
            ...roleData,
            id: roleId,
            permissions: permissions
                .filter((permission: any) => permission.isChecked)
                .map((permission: any) => permission.name),
        };

        const createOrUpdateRole = async () => {
            if (btnText === "Create") {
                return await createRole(commonObj).unwrap();
            } else {
                return await updateRole({
                    id: roleId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateRole, (response) => {
            if (response?.success || response?.data?.success) {
                setRoleData({});
                navigate(AppRoutes.Roles.path);
            }
        });
    }

    const handleCheckAll = (e: any) => {
        const { name, checked } = e.target;
        let tempPer = permissions.map((el: any) => el.name.split(' ')[1] === name ? {
            ...el,
            isChecked: checked
        } : el);
        setPermissions(tempPer);
    }

    const handleChange = (e: any) => {
        const { name, checked } = e.target;
        if (name.includes("all")) {
            let id = e.target.id;
            let tempPer = permissions.map((el: any) => el.name.split(' ')[0] === id ? {
                ...el,
                isChecked: checked
            } : el);
            setPermissions(tempPer);
        } else {
            let tempPer = permissions.map((el: any) => el.name === name ? {
                ...el,
                isChecked: checked
            } : el);
            setPermissions(tempPer);
        }
    };

    return (
        <>
            {!permissionsQuery.data ?
                <Preloader /> :
                <div className="px-4 py-2">
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        <Breadcrumb crumbs={crumbs} />
                        {btnText === "Update" && hasPermission(["ROL-C"]) &&
                            < IconButton
                                className={'h-[35px] min-w-[52px] rounded-[11px]'}
                                icon={<FaCopy size={14} />
                                }
                                hasBackground={true}
                                onClick={(e: any) => {
                                    e.preventDefault();
                                    localStorage.setItem("roleId", roleId || "");
                                    navigate(AppRoutes.CreateRole.path);
                                }}
                                title={"Copy Role"}
                            />
                        }
                    </div>
                    <div className="flex justify-center mt-3">
                        <div
                            className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                            <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                            <Form onSubmit={handleSubmit} fields={
                                <>
                                    <div className="grid grid-cols-12">

                                        <InputField
                                            name={"name"}
                                            label="Role Name"
                                            onChange={handleOnChange}
                                            value={roleData?.name || ""}
                                            type="text"
                                            placeholder="Enter Name..."
                                            required={true}
                                            disabled={isReadOnly}
                                        />

                                        <InputField
                                            name={"authorityLevel"}
                                            label="Authority Level"
                                            onChange={handleOnChange}
                                            value={roleData?.authorityLevel || ""}
                                            type="number"
                                            placeholder="Enter Authority Level..."
                                            required={true}
                                            disabled={isReadOnly}
                                        />

                                        <div className="col-span-12 md:grid md:grid-cols-12 gap-3 text-surface-0">
                                            <div className="col-span-3 sm:mt-2 lg-mt-0 mt-2">
                                                <div className="text-surface-0 pt-2">*Permissions</div>
                                            </div>
                                            <div className="col-span-9 relative mt-2">
                                                <div className='row'>
                                                    <div className="col-12">
                                                        <div className="border border-surface-5 rounded-md">
                                                            <div className="grid  grid-cols-12">
                                                                <div className='col-span-4'></div>
                                                                <div className='col-span-8'>
                                                                    <ul className="m-0 flex justify-between p-3 text-right text-surface-0">
                                                                        {["All", "View", "Read", "Create", "Update", "Delete", "Bulk"].map((permission) => (
                                                                            <li key={permission}>{permission}</li>
                                                                        ))}
                                                                    </ul>

                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="with-max-r h-96 overflow-x-scroll custom_scrollbar text-surface-0">
                                                            <div
                                                                className="border border-surface-5 rounded-md text-surface-0">
                                                                <div className="grid grid-cols-12 text-surface-0">
                                                                    <div className="col-span-4 mt-2"><span
                                                                        className="ml-2 mt-2"></span></div>
                                                                    <div className="col-span-8">
                                                                        <ul className="checks flex justify-between text-right p-3 m-0">
                                                                            <li className={"ml-2 opacity-0"}>1
                                                                            </li>
                                                                            {["View", "Read", "Create", "Update", "Delete", "Bulk"].map((permission) => (
                                                                                <li>
                                                                                    <InputField
                                                                                        name={permission}
                                                                                        onChange={handleCheckAll}
                                                                                        type="checkbox"
                                                                                        checked={!allPermissionCheckedOnColumns(permissions, permission)}
                                                                                        labelPosition={"top"}
                                                                                        disabled={isReadOnly}
                                                                                    />
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {permissionLabel.map((curPermission: any) => {
                                                                return (<>
                                                                    <div className="border border-surface-5 rounded-md">
                                                                        <div className='grid grid-cols-12'>
                                                                            <div className='col-span-4 mt-2'>
                                                                                <span
                                                                                    className="ml-2 mt-2 text-sm">{curPermission}</span>
                                                                            </div>
                                                                            <div className='col-span-8'>
                                                                                <ul className="m-0 checks flex justify-between p-3 text-right">
                                                                                    <li>
                                                                                        <input type="checkbox"
                                                                                            id={curPermission}
                                                                                            name="allSelect"
                                                                                            checked={!allPermissionChecked(permissions, curPermission)}
                                                                                            onChange={handleChange}
                                                                                            disabled={isReadOnly}
                                                                                        />
                                                                                    </li>
                                                                                    {["View", "Read", "Create", "Update", "Delete", "Bulk"].map((cur) => (
                                                                                        <li>
                                                                                            <InputField
                                                                                                type="checkbox"
                                                                                                onChange={handleChange}
                                                                                                name={curPermission + ` ${cur}`}
                                                                                                value={curPermission + ` ${cur}`}
                                                                                                checked={filterPermission(permissions, curPermission, ` ${cur}`)?.isChecked || false}
                                                                                                disabled={filterPermission(permissions, curPermission, ` ${cur}`) === undefined || isReadOnly}
                                                                                                labelPosition={"top"}
                                                                                            />
                                                                                        </li>
                                                                                    ))}

                                                                                </ul>
                                                                            </div>

                                                                        </div>
                                                                    </div>
                                                                </>)
                                                            })}
                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>

                                    </div>

                                    {!isReadOnly &&

                                        <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                            <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                            </div>
                                            <div className="relative col-span-9">
                                                <div className={"mt-4 flex"}>
                                                    <ButtonTray
                                                        buttons={[
                                                            {
                                                                buttonProps: {
                                                                    btnText: "Cancel",
                                                                    onClick: () => navigate(AppRoutes.Roles.path),
                                                                    type: "close",
                                                                },
                                                                buttonType: "button"
                                                            },
                                                            {
                                                                buttonProps: {
                                                                    btnText: "Submit",
                                                                    type: "submit",
                                                                    isLoading: (createRoleApiResponse.isLoading || updateRoleApiResponse.isLoading),

                                                                },
                                                                buttonType: "button",

                                                            }
                                                        ]}
                                                        align={"center"}
                                                        gap={2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </>
                            } />

                        </div>
                    </div>
                </div>
            }
        </>
    )
}
export default RoleForm;